import { EMPTY_CELL } from './escape-html';

/**
 * A date column where having no date is a normal answer, not missing data.
 *
 * The CRUD's built-in `date` type renders nothing for an absent value, which reads as
 * a broken cell rather than "we don't have one". A dash says what is actually true,
 * and matches the Issued column on the permit lists.
 *
 * TAKES `unknown` ON PURPOSE. The CRUD decides what to hand a custom column with:
 *
 *     item[colValue] !== undefined ? item[colValue] : item
 *
 * and the API omits null properties entirely - so a blacklist entry with no date of
 * birth has no `dateOfBirth` key at all and the CRUD passes THE WHOLE ROW OBJECT
 * instead. A `string | null` signature is a lie the compiler cannot catch. Getting
 * this wrong throws inside change detection, which does not spoil one cell: Angular
 * abandons the rest of the view, so the table renders one row and looks like a paging
 * bug. That is not hypothetical - it is exactly how the "Issued by" column failed.
 */
export function optionalDate(value: unknown): string {
    if (typeof value !== 'string' || !value.trim()) {
        return EMPTY_CELL;
    }

    // Read the calendar date straight off the string rather than through Date.
    //
    // The API sends a date-only value as a full timestamp with an offset -
    // "1985-03-02T00:00:00+09:30" - and `new Date()` resolves that to an instant, which
    // toLocaleDateString then renders in the VIEWER'S timezone. For anyone west of
    // Darwin that is the day before. A date of birth has no time and no timezone; it is
    // the same day wherever it is read, so it is never converted.
    const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
    if (!iso) {
        return EMPTY_CELL;
    }

    const [, year, month, day] = iso;

    // Matches the format the other date columns use, with a full year - this is a date
    // of birth, where a two-digit year is genuinely ambiguous.
    return `${Number(day)}/${Number(month)}/${year}`;
}
